import { Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { LogOut } from 'lucide-react-native';
import { useAuth } from '@/lib/supabase';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { useAlert } from '@/components/AlertDialog';
import { handleError } from '@/lib/errors';

/**
 * Sign-out affordance for the founder onboarding screens.
 *
 * Those screens use custom mobile headers (not `OnboardingLayout`), so they had
 * no way out — a user who wanted to switch accounts or bail was stranded. This
 * drops into the header's right slot (where a `w-10` spacer used to balance the
 * back button), confirms first, then signs out and returns to login. Onboarding
 * progress is persisted to `profiles.onboarding_step`, so a resume is intact.
 */
export function OnboardingSignOut() {
  const router = useRouter();
  const { signOut } = useAuth();
  const colors = useSystemThemeColors();
  const { show } = useAlert();

  const onPress = async () => {
    const result = await show({
      type: 'confirm',
      title: 'Sign out?',
      message: 'Your progress is saved — you can pick up onboarding where you left off next time you sign in.',
      buttons: [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Sign out', style: 'destructive' },
      ],
    });
    if (result !== 1) return;
    try {
      await signOut();
      router.replace('/login');
    } catch (err) {
      handleError(err, { context: 'OnboardingSignOut.signOut' });
    }
  };

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel="Sign out"
      hitSlop={8}
      className="w-10 h-10 rounded-full items-center justify-center"
    >
      <LogOut size={20} color={colors.text.tertiary} />
    </Pressable>
  );
}
